import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import {
  FEATURES,
  ACTIONS,
  type FeatureKey,
  type ActionKey,
  type ActionPermissions,
  type RoleDoc,
  roleSave,
  emptyPermissions,
} from '../../lib/permissions';

export interface RoleDrawerProps {
  open: boolean;
  onClose: () => void;
  role: RoleDoc | null;
  onSaved: () => Promise<void>;
}

interface FeatureGroup {
  nameKey: string;
  features: FeatureKey[];
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    nameKey: 'roles.group.whatsapp',
    features: ['connect', 'templates', 'broadcasts'],
  },
  {
    nameKey: 'roles.group.crm',
    features: ['subscribers', 'team'],
  },
  {
    nameKey: 'roles.group.inbox',
    features: ['inbox', 'inbox_number_mask', 'inbox_advanced'],
  },
  {
    nameKey: 'roles.group.automation',
    features: ['bots', 'ai'],
  },
  {
    nameKey: 'roles.group.commerce',
    features: ['catalog', 'ads'],
  },
  {
    nameKey: 'roles.group.integrations',
    features: ['integrations', 'google', 'api'],
  },
  {
    nameKey: 'roles.group.settings',
    features: ['billing', 'settings'],
  },
];

export function RoleDrawer({ open, onClose, role, onSaved }: RoleDrawerProps) {
  const { t } = useI18n();
  const isEditing = Boolean(role);
  const isBuiltIn = Boolean(role?.builtIn);

  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<Record<FeatureKey, ActionPermissions>>(emptyPermissions());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (role) {
      setName(role.name);
      setPermissions(role.permissions || emptyPermissions());
    } else {
      setName('');
      setPermissions(emptyPermissions());
    }
    setErr(null);
  }, [role, open]);

  function handleToggle(feature: FeatureKey, action: ActionKey) {
    if (isBuiltIn) return;
    setPermissions((prev) => ({
      ...prev,
      [feature]: {
        ...prev[feature],
        [action]: !prev[feature]?.[action],
      },
    }));
  }

  function handleToggleRow(feature: FeatureKey) {
    if (isBuiltIn) return;
    const current = permissions[feature];
    const allOn = ACTIONS.every((a) => current?.[a]);
    setPermissions((prev) => ({
      ...prev,
      [feature]: {
        create: !allOn,
        update: !allOn,
        delete: !allOn,
        special: !allOn,
      },
    }));
  }

  function handleToggleColumn(action: ActionKey) {
    if (isBuiltIn) return;
    const allOn = FEATURES.every((f) => permissions[f]?.[action]);
    setPermissions((prev) => {
      const next = { ...prev };
      for (const f of FEATURES) {
        next[f] = {
          ...next[f],
          [action]: !allOn,
        };
      }
      return next;
    });
  }

  function handleSelectAll() {
    if (isBuiltIn) return;
    const allOn = FEATURES.every((f) => ACTIONS.every((a) => permissions[f]?.[a]));
    setPermissions(() => {
      const next = {} as Record<FeatureKey, ActionPermissions>;
      for (const f of FEATURES) {
        next[f] = {
          create: !allOn,
          update: !allOn,
          delete: !allOn,
          special: !allOn,
        };
      }
      return next;
    });
  }

  async function handleSave() {
    if (!name.trim()) {
      setErr(t('roles.nameRequired'));
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await roleSave({
        id: role?.id,
        name: name.trim(),
        permissions,
      });
      await onSaved();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEditing ? (isBuiltIn ? `${name} (${t('roles.builtIn')})` : t('roles.editTitle', { name })) : t('roles.createTitle')}
      size="lg"
    >
      <div className="space-y-5">
        {/* Role Name */}
        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('roles.name')}
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isBuiltIn}
            placeholder={t('roles.namePlaceholder')}
            className="mt-1 w-full rounded-[9px] border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand disabled:bg-surface/50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        {isBuiltIn && (
          <div className="rounded-card border border-brand/20 bg-brand-50/50 p-3.5 text-xs text-brand-900 dark:border-brand-900/60 dark:bg-brand-950/30 dark:text-brand-tint">
            ℹ️ {t('roles.builtInNotice')}
          </div>
        )}

        {/* Global Shortcuts */}
        {!isBuiltIn && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-3 dark:border-slate-800">
            <span className="text-xs font-bold text-ink dark:text-slate-200">
              {t('roles.permissionMatrix')}
            </span>
            <Button size="xs" variant="secondary" onClick={handleSelectAll}>
              {t('roles.toggleAll')}
            </Button>
          </div>
        )}

        {err && (
          <div className="rounded-[9px] bg-danger-50 px-3 py-2 text-xs font-semibold text-danger dark:bg-rose-950/40 dark:text-rose-300">
            {err}
          </div>
        )}

        {/* Permissions Matrix Table */}
        <div className="overflow-hidden rounded-card border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="w-full text-start text-xs">
              <thead className="border-b border-line bg-surface/50 text-ink-3 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2.5 text-start font-semibold min-w-[160px]">
                    {t('roles.feature')}
                  </th>
                  {ACTIONS.map((act) => (
                    <th key={act} className="px-3 py-2.5 text-center font-semibold">
                      <div className="flex flex-col items-center gap-1">
                        <span>{t(`roles.action.${act}`)}</span>
                        {!isBuiltIn && (
                          <button
                            type="button"
                            onClick={() => handleToggleColumn(act)}
                            className="text-[10px] text-brand hover:underline dark:text-brand-tint"
                          >
                            {t('roles.all')}
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line dark:divide-slate-800">
                {FEATURE_GROUPS.map((group) => (
                  <tr key={group.nameKey} className="contents">
                    {/* Section Header Row */}
                    <tr className="bg-surface/30 dark:bg-slate-800/20">
                      <td
                        colSpan={5}
                        className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand dark:text-brand-tint"
                      >
                        {t(group.nameKey)}
                      </td>
                    </tr>

                    {/* Features in this group */}
                    {group.features.map((feat) => {
                      const cur = permissions[feat];
                      return (
                        <tr
                          key={feat}
                          className="hover:bg-surface/30 dark:hover:bg-slate-800/20 transition-colors"
                        >
                          <td className="px-4 py-2.5 text-start font-medium text-ink dark:text-slate-200">
                            <div className="flex items-center justify-between gap-2">
                              <span>{t(`roles.feat.${feat}`)}</span>
                              {!isBuiltIn && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleRow(feat)}
                                  className="text-[10px] text-brand hover:underline dark:text-brand-tint"
                                >
                                  {t('roles.all')}
                                </button>
                              )}
                            </div>
                          </td>

                          {ACTIONS.map((act) => (
                            <td key={act} className="px-3 py-2.5 text-center align-middle">
                              <input
                                type="checkbox"
                                checked={Boolean(cur?.[act])}
                                disabled={isBuiltIn}
                                onChange={() => handleToggle(feat, act)}
                                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand dark:border-slate-700 dark:bg-slate-800"
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Actions */}
        {!isBuiltIn && (
          <div className="flex justify-end gap-2 pt-2 border-t border-line dark:border-slate-800">
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={saving || !name.trim()} onClick={handleSave}>
              {saving ? t('roles.saving') : t('roles.save')}
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default RoleDrawer;
